import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const CONTEXT_LABELS = {
  'hardware-request':             'Hardware Request',
  'overtime-decision':            'Overtime Decision',
  'talent-vacancy-decision':      'Vacancy Decision',
  'suggestion-test':              'Suggestion Test',
  'suggestion-rework':            'Suggestion Re-work',
  'milestone-reminder':           'Milestone Reminder',
  'leave-request':                'Leave Request',
  'leave-decision':               'Leave Decision',
  'desk-booking':                 'Desk Booking',
  'timesheet-reminder':           'Timesheet Reminder',
  'timesheet-approval-request':   'Timesheet Approval Request',
  'timesheet-decision':           'Timesheet Decision',
  'sow-issuance':                 'SOW Issuance',
  'contractor-welcome':           'Contractor Welcome',
  'supplier-qa-digest':           'Supplier QA Digest',
  'weekly-hours-reminder':        'Weekly Hours Reminder',
  'monday-time-chase':            'Monday Time Chase',
  'people-dates-digest':          'People Dates Digest',
  'leaver-actions':               'Leaver Actions',
  'portal-invite':                'Portal Invite',
  'autoentry-forward':            'AutoEntry Forward',
  'po-delivered':                 'PO Delivered',
  'plow-access-request':          'PLOW Access Request',
  'talent-regret':                'Talent Regret',
  'talent-interview-panel':       'Interview Panel Invitation',
  'talent-interview-candidate':   'Interview Invitation (Candidate)',
  'talent-offer':                 'Offer of Employment',
  'cot_receipt_reminder':         'Card Receipt Reminder',
  'cot_receipt_nudge':            'Card Receipt Nudge',
  'cot_receipt_escalation':       'Card Receipt Escalation',
  'cot_receipt_reminder_finance': 'Card Receipts — Finance Summary',
};

const PROVIDER_LABELS = { resend: 'Resend', m365: 'M365' };

const STATUS_STYLES = {
  sent:       'bg-green-100 text-green-700',
  failed:     'bg-red-100 text-red-600',
  paused:     'bg-amber-100 text-amber-700',
  suppressed: 'bg-gray-100 text-gray-500',
};

// Full catalog of every email type the system sends
const TEMPLATE_CATALOG = [
  {
    context: 'leave-request',
    label: 'Leave Request',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a team member submits a leave request' },
    recipients: 'The leave approver for that person',
    dataPoints: ['Requester name', 'Leave type', 'Start & end dates', 'Day count', 'Reason (if provided)'],
  },
  {
    context: 'leave-decision',
    label: 'Leave Decision',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a leave request is approved or rejected' },
    recipients: 'The person who requested leave',
    dataPoints: ['Leave type', 'Dates', 'Decision (approved/rejected)', 'Approver name', 'Calendar invite (.ics) attachment if approved'],
  },
  {
    context: 'desk-booking',
    label: 'Desk Booking Confirmation',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a desk booking is confirmed' },
    recipients: 'The person who made the booking',
    dataPoints: ['Office location', 'Desk label', 'Date(s)', 'Slot (full day / AM / PM)'],
  },
  {
    context: 'hardware-request',
    label: 'Hardware Request',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a hardware request is submitted by any staff member' },
    recipients: 'All PMO and admin users',
    dataPoints: ['Product name', 'Part number', 'Quantity', 'Total cost', 'Required-by date', 'Notes'],
  },
  {
    context: 'po-delivered',
    label: 'PO Delivered',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a purchase order is marked as delivered' },
    recipients: 'The person who requested the goods — taken from the linked hardware request, or an address added by hand when the PO has no linked request',
    dataPoints: ['PO number', 'Supplier', 'Delivery location', 'Order value', 'Item list with quantities'],
  },
  {
    context: 'plow-access-request',
    label: 'PLOW Access Request',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When somebody who is not on a PLOW asks to see it' },
    recipients: 'Every assigned editor of that PLOW',
    dataPoints: ['Who asked', 'Board name', 'Their reason', 'Link to the board, where viewer access is one click'],
  },
  // ── Talent Acquisition ──
  // Three of these four go to a candidate rather than to a member of staff, so
  // the wording is the company's to an outsider and carries no link into ops.
  // All four were sending before they were listed here, which is how a message
  // ends up outside the house layout with nobody able to preview or pause it.
  {
    context: 'talent-regret',
    label: 'Talent Regret',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a candidate is rejected and the rejecter asks for them to be told' },
    recipients: 'The candidate who applied',
    note: 'Never carries the internal rejection reason. The closing paragraph changes depending on whether the candidate agreed to be kept on file.',
    dataPoints: ['Candidate first name', 'Vacancy title', 'Whether they consented to be held for future roles'],
  },
  {
    context: 'talent-interview-panel',
    label: 'Interview Panel Invitation',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When an interview is booked, to each panel member in turn' },
    recipients: 'Every member of the interview panel, at their work address',
    note: 'The same booking also opens an Action for each panel member, so the feedback request arrives twice over by design.',
    dataPoints: ['Candidate name', 'Vacancy title', 'Interview type', 'Date, time and duration', 'Location', 'Who else is on the panel', 'The brief, where one was given', 'Link to the feedback form'],
  },
  {
    context: 'talent-interview-candidate',
    label: 'Interview Invitation (Candidate)',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When an interview is booked and the candidate has an email address on file' },
    recipients: 'The candidate being interviewed',
    dataPoints: ['Candidate first name', 'Vacancy title', 'Interview type', 'Date, time and duration', 'Location', 'Names of the panel they will meet'],
  },
  {
    context: 'talent-offer',
    label: 'Offer of Employment',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When an approved offer is marked as sent' },
    recipients: 'The candidate receiving the offer',
    note: 'A draft ahead of the formal contract, which follows separately. An offer above the band cannot reach this point until it has been approved.',
    dataPoints: ['Candidate first name', 'Vacancy title', 'Salary', 'Proposed start date', 'Security clearance target', 'Any conditions attached to the offer'],
  },
  // ── Company card receipts ──
  // Four messages about one thing, listed together because that is how they are read: a
  // person gets the first, ignores it three times, and then their manager gets the third.
  // They were missing from this catalogue entirely, which is how all four came to be sent
  // outside the house layout without anybody noticing.
  {
    context: 'cot_receipt_reminder',
    label: 'Card Receipt Reminder',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'Every Monday at 08:20 UTC', cron: '20 8 * * 1' },
    recipients: 'Each cardholder with a transaction older than the grace period and no receipt — their own list only',
    note: 'Grace period and escalation threshold are set on Card spend → Settings',
    dataPoints: ['First name', 'Their outstanding transactions (date, merchant, amount)', 'VAT that cannot be reclaimed', 'How many of their transactions are already receipted', 'Oldest outstanding date'],
  },
  {
    context: 'cot_receipt_nudge',
    label: 'Card Receipt Nudge',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When finance chase one transaction from the card spend screen' },
    recipients: 'The cardholder the transaction belongs to',
    dataPoints: ['Who in finance is asking', 'The single transaction', 'VAT on it', 'Optional message from the sender', 'How many others they have outstanding'],
  },
  {
    context: 'cot_receipt_escalation',
    label: 'Card Receipt Escalation',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'With the Monday reminder run, once a transaction passes the reminder threshold', cron: '20 8 * * 1' },
    recipients: 'Admin and finance',
    note: 'Sent once per transaction, never repeated — an escalation on a schedule becomes as ignorable as the reminder it replaced',
    dataPoints: ['Each person by name', 'Their unanswered transactions and how many times each was asked about', 'Total VAT at stake', 'Spend on cards mapped to nobody'],
  },
  {
    context: 'cot_receipt_reminder_finance',
    label: 'Card Receipts — Finance Summary',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'With the Monday reminder run, only when something needs finance', cron: '20 8 * * 1' },
    recipients: 'Finance and billing',
    note: 'Carries only what finance can act on: transactions chased past the escalation threshold, and spend on unmapped cards. Nothing to act on means no email.',
    dataPoints: ['Transactions asked about repeatedly, by person', 'Spend on cards mapped to nobody', 'VAT that cannot be reclaimed'],
  },
  {
    context: 'overtime-decision',
    label: 'Overtime Decision',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When an overtime or TOIL request is approved or rejected' },
    recipients: 'The person who requested the overtime',
    dataPoints: ['Request type', 'Date', 'Decision', 'Rejection reason (if applicable)'],
  },
  {
    context: 'talent-vacancy-decision',
    label: 'Vacancy Decision',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a vacancy is approved or rejected in Talent Acquisition' },
    recipients: 'The person who raised the vacancy, and the hiring manager where that is somebody else',
    dataPoints: ['Reference', 'Role title', 'Decision', 'Who decided it', 'Approval note or rejection reason'],
  },
  {
    context: 'milestone-reminder',
    label: 'Milestone Health Reminder',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'Every weekday at 08:00 UTC', cron: '0 8 * * 1-5' },
    recipients: 'Project managers and SOW owners with stale or overdue milestones',
    dataPoints: ['Milestone codes', 'SOW references', 'Forecast dates', 'Health update status for each'],
  },
  {
    context: 'timesheet-reminder',
    label: 'Timesheet Submission Reminder',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'Every Sunday at 18:00 UTC', cron: '0 18 * * 7' },
    recipients: 'Active contractors who have not yet submitted a timesheet for the current week',
    dataPoints: ['Contractor first name', 'Week date range'],
  },
  {
    context: 'timesheet-approval-request',
    label: 'Timesheet Approval Request',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a contractor submits a timesheet' },
    recipients: 'The timesheet approver (internal staff or client contact)',
    dataPoints: ['Contractor name', 'Week start date', 'Contract / SOW reference'],
  },
  {
    context: 'timesheet-decision',
    label: 'Timesheet Decision',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When all approval decisions for a timesheet are resolved' },
    recipients: 'The contractor who submitted the timesheet',
    dataPoints: ['Week start date', 'Decision (approved/rejected)', 'Rejection reason if applicable'],
  },
  {
    context: 'supplier-qa-digest',
    label: 'Supplier QA Digest',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'Every Monday at 07:00 UTC', cron: '0 7 * * 1' },
    recipients: 'Configured digest recipients (Settings → Integrations), or all admins if not set',
    dataPoints: ['Supplier names', 'Missing documents', 'Expired documents', 'Documents expiring within warning window'],
  },
  {
    context: 'weekly-hours-reminder',
    label: 'Daily Time-Log Reminder',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'Tuesday to Friday at 09:00 UTC', cron: '0 9 * * 2-5' },
    recipients: 'Internal staff with no time logged for the previous working day',
    note: 'Can be paused via Settings → Integrations. Does not run Mondays: the Monday Time Chase covers the whole of last week instead',
    dataPoints: ['Staff first name', 'Previous working day'],
  },
  {
    context: 'monday-time-chase',
    label: 'Monday Time Chase',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'The day a week closes, at 7am UK time — usually Monday, slid to the next working day over bank holidays (ticked at 06:00 and 07:00 UTC every weekday; the job runs only on the closing day, on whichever tick is 7am in London)', cron: '0 6 * * 1-5' },
    recipients: 'Internal staff whose last week is short of their expected weekly hours (pro-rated for bank holidays and approved leave, half days included)',
    note: 'The last warning before the week locks: entries close at the end of that day',
    dataPoints: ['Staff first name', 'Week dates', 'Hours logged', 'Hours expected'],
  },
  {
    context: 'people-dates-digest',
    label: 'People Dates Digest',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'The first working day of each week, at 07:30 UK time — usually Monday, slid to the next working day over a bank holiday (ticked at 06:30 and 07:30 UTC every weekday; the job runs on one day a week, on whichever tick is 07:30 in London)', cron: '30 6 * * 1-5' },
    recipients: 'Configured recipients (app setting people_dates_digest_recipients), or everyone holding the PMO role, or all admins if there is no PMO',
    note: 'One email a week. Sends nothing at all in a week with no dates to report',
    dataPoints: ['Birthdays falling in the week', 'The working day to mark a weekend or bank holiday date on', 'Fifth and tenth work anniversaries', 'Significant birthdays (21, 30, 40, 50, 60, 70, 75, 80) three months ahead', 'Count of people with no date of birth or start date recorded'],
  },
  {
    context: 'leaver-actions',
    label: 'Leaver Actions',
    provider: 'resend',
    trigger: { type: 'cron', desc: 'Daily at 18:00 UTC (19:00 UK time in summer), for each offboarding record whose Last Working Day is today, or up to seven days ago if it was missed', cron: '0 18 * * *' },
    recipients: 'Configured recipients (app setting leaver_notification_recipients), or all admins if none are set',
    note: 'One email per leaver. A second email lists anybody whose End of Employment is today but who was not actioned, with the reason',
    dataPoints: ['Leaver name and last working day', 'Microsoft 365: account disabled, sign-in sessions revoked, groups left, anything still to clear', 'Ops: status archived (and what it was), End of Employment set, checklist steps ticked', 'Checklist steps still open'],
  },
  {
    context: 'sow-issuance',
    label: 'SOW Issuance',
    provider: 'm365',
    trigger: { type: 'event', desc: 'When a Statement of Work is manually issued to a client' },
    recipients: 'Client contacts specified at the time of issue (with optional CC)',
    note: 'Sent via Microsoft 365 Graph API — not Resend — so a copy is retained in the Sent Items mailbox for audit',
    dataPoints: ['SOW reference', 'Custom message', 'SOW PDF as attachment'],
  },
  {
    context: 'suggestion-test',
    label: 'Suggestion Ready for Testing',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a suggestion is moved to "Ready for testing" status' },
    recipients: 'The person who submitted the suggestion',
    dataPoints: ['Suggestion title', 'Testing instructions'],
  },
  {
    context: 'suggestion-rework',
    label: 'Suggestion Needs Rework',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a suggestion is returned for rework' },
    recipients: 'The person who submitted the suggestion',
    dataPoints: ['Suggestion title', 'Feedback / reason for rework'],
  },
  {
    context: 'portal-invite',
    label: 'Client Portal Invite',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a client contact is granted access to the contractor approvals portal' },
    recipients: 'The client contact being given portal access',
    dataPoints: ['Contact first name', 'Portal login link'],
  },
  {
    context: 'contractor-welcome',
    label: 'Contractor Welcome',
    provider: 'm365',
    trigger: { type: 'event', desc: 'When a contractor is invited to the resource portal' },
    recipients: 'The contractor',
    dataPoints: ['Contractor first name', 'Portal login link'],
  },
  {
    context: 'autoentry-forward',
    label: 'AutoEntry Forward',
    provider: 'resend',
    trigger: { type: 'event', desc: 'When a procurement email is forwarded to AutoEntry for processing' },
    recipients: 'The AutoEntry ingestion mailbox',
    dataPoints: ['Forwarded document details'],
  },
];

// ─── Utilities ────────────────────────────────────────────────────────────────

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtRelative(iso) {
  if (!iso) return null;
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  const diff = Date.now() - d.getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Email Log Tab ────────────────────────────────────────────────────────────

function EmailLogTab() {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [limit, setLimit]       = useState(50);
  const [search, setSearch]     = useState('');
  const [provider, setProvider] = useState('');
  const [context, setContext]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [resending, setResending] = useState({});
  const [resendMsg, setResendMsg] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search)   params.search   = search;
      if (provider) params.provider = provider;
      if (context)  params.context  = context;
      const data = await api.get('sent-emails', params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch { setItems([]); setTotal(0); }
    setLoading(false);
  }, [page, limit, search, provider, context]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  async function handleResend(item) {
    if (!item.context || item.provider === 'm365') return;
    setResending(r => ({ ...r, [item.id]: true }));
    setResendMsg(r => ({ ...r, [item.id]: null }));
    try {
      await api.post(`email-templates/${item.context}/test`, { test_email: item.to_addr });
      setResendMsg(r => ({ ...r, [item.id]: { ok: true, text: `Resent to ${item.to_addr}` } }));
    } catch (e) {
      setResendMsg(r => ({ ...r, [item.id]: { ok: false, text: e.message || 'Failed' } }));
    }
    setResending(r => ({ ...r, [item.id]: false }));
    setTimeout(() => setResendMsg(r => { const n = { ...r }; delete n[item.id]; return n; }), 5000);
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <p className="text-[9px] text-gray-500">
          All outbound emails sent via Resend and M365 — {total.toLocaleString()} record{total !== 1 ? 's' : ''}
        </p>
        <button onClick={load} className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded hover:bg-gray-50">
          ↺ Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="text"
          placeholder="Search subject, recipient…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded px-2 py-1 text-[9px] focus:outline-none focus:border-gold w-52"
        />
        <select value={provider} onChange={e => { setProvider(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded px-2 py-1 text-[9px] focus:outline-none focus:border-gold">
          <option value="">All providers</option>
          <option value="resend">Resend</option>
          <option value="m365">M365</option>
        </select>
        <select value={context} onChange={e => { setContext(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded px-2 py-1 text-[9px] focus:outline-none focus:border-gold">
          <option value="">All types</option>
          {Object.entries(CONTEXT_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
          className="border border-gray-200 rounded px-2 py-1 text-[9px] focus:outline-none focus:border-gold ml-auto">
          {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} per page</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full text-[9px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 text-gray-500 font-bold uppercase">
              <th className="text-left px-3 py-2">Sent</th>
              <th className="text-left px-3 py-2">Provider</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">To</th>
              <th className="text-left px-3 py-2">Subject</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-400">No emails found</td></tr>
            ) : items.map(item => (
              <>
                <tr key={item.id}
                  onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                  className="border-b border-gray-50 hover:bg-gold/5 cursor-pointer">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500">{fmt(item.sent_at)}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded font-bold ${item.provider === 'm365' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                      {PROVIDER_LABELS[item.provider] || item.provider}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {CONTEXT_LABELS[item.context] || item.context || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-700 max-w-[180px] truncate" title={item.to_addr}>{item.to_addr}</td>
                  <td className="px-3 py-2 text-gray-700 max-w-[260px] truncate" title={item.subject}>{item.subject}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded font-bold ${STATUS_STYLES[item.status] || 'bg-gray-100 text-gray-600'}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    {item.context && item.provider !== 'm365' ? (
                      <div>
                        <button
                          onClick={() => handleResend(item)}
                          disabled={!!resending[item.id]}
                          className="text-[8px] font-semibold px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40"
                        >
                          {resending[item.id] ? '…' : 'Resend'}
                        </button>
                        {resendMsg[item.id] && (
                          <div className={`text-[7px] mt-0.5 font-semibold ${resendMsg[item.id].ok ? 'text-green-600' : 'text-red-500'}`}>
                            {resendMsg[item.id].text}
                          </div>
                        )}
                      </div>
                    ) : item.provider === 'm365' ? (
                      <span className="text-[7px] text-gray-300" title="M365 emails cannot be resent from here">—</span>
                    ) : null}
                  </td>
                </tr>
                {expanded === item.id && (
                  <tr key={`${item.id}-detail`} className="bg-gray-50 border-b border-gray-100">
                    <td colSpan={7} className="px-3 py-2">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[8px]">
                        <div><span className="text-gray-400 uppercase font-bold">From: </span><span className="font-mono">{item.from_addr || '—'}</span></div>
                        <div><span className="text-gray-400 uppercase font-bold">To: </span><span className="font-mono">{item.to_addr}</span></div>
                        <div className="col-span-2"><span className="text-gray-400 uppercase font-bold">Subject: </span>{item.subject}</div>
                        {item.error && <div className="col-span-2 text-red-600"><span className="font-bold">Error: </span>{item.error}</div>}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2">
          <span className="text-[8px] text-gray-400">
            {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1}
              className="px-2 py-0.5 text-[8px] border border-gray-200 rounded disabled:opacity-30 hover:bg-gray-50">«</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-2 py-0.5 text-[8px] border border-gray-200 rounded disabled:opacity-30 hover:bg-gray-50">← Prev</button>
            <span className="text-[8px] text-gray-500 px-2">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-2 py-0.5 text-[8px] border border-gray-200 rounded disabled:opacity-30 hover:bg-gray-50">Next →</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
              className="px-2 py-0.5 text-[8px] border border-gray-200 rounded disabled:opacity-30 hover:bg-gray-50">»</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────

function PreviewModal({ context, onClose }) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/email-templates/${context}/preview`)
      .then(r => r.text())
      .then(h => { setHtml(h); setLoading(false); })
      .catch(() => { setHtml('<p>Preview unavailable</p>'); setLoading(false); });
  }, [context]);

  return (
    <Modal
      wide
      title={`Preview — ${CONTEXT_LABELS[context] || context}`}
      onClose={onClose}
      footer={
        <button onClick={onClose} className="px-3 py-1.5 text-[9px] font-semibold border border-gray-200 rounded hover:bg-gray-50">
          Close
        </button>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-40 text-[9px] text-gray-400">Loading preview…</div>
      ) : (
        <iframe
          srcDoc={html}
          className="w-full border-0"
          style={{ minHeight: '500px' }}
          title={`Preview of ${context} email`}
          sandbox="allow-same-origin"
        />
      )}
    </Modal>
  );
}

// ─── Template Card ────────────────────────────────────────────────────────────

function TemplateCard({ template, stats, config, onSave }) {
  const [open, setOpen]         = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Config form state
  const [headerColor, setHeaderColor]       = useState(config?.header_color || '');
  const [extraRecipients, setExtraRecipients] = useState(config?.extra_recipients || '');
  const [customNote, setCustomNote]         = useState(config?.custom_note || '');
  const [isPaused, setIsPaused]             = useState(!!config?.is_paused);
  const [saving, setSaving]                 = useState(false);
  const [saveMsg, setSaveMsg]               = useState('');

  // Test send state
  const [testEmail, setTestEmail]  = useState('');
  const [testing, setTesting]      = useState(false);
  const [testMsg, setTestMsg]      = useState('');

  // Sync from config prop changes
  useEffect(() => {
    setHeaderColor(config?.header_color || '');
    setExtraRecipients(config?.extra_recipients || '');
    setCustomNote(config?.custom_note || '');
    setIsPaused(!!config?.is_paused);
  }, [config]);

  const triggerBadge = template.trigger.type === 'cron'
    ? <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-bold">Scheduled</span>
    : <span className="px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded font-bold">Event</span>;

  const providerBadge = template.provider === 'm365'
    ? <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-bold border border-blue-200">M365</span>
    : <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded font-bold border border-purple-200">Resend</span>;

  const isM365 = template.provider === 'm365';

  async function handleSave() {
    setSaving(true); setSaveMsg('');
    try {
      await api.put(`email-templates/${template.context}`, {
        header_color:     headerColor.trim() || null,
        extra_recipients: extraRecipients.trim() || null,
        custom_note:      customNote.trim() || null,
        is_paused:        isPaused,
      });
      setSaveMsg('Saved');
      onSave?.();
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) {
      // The server names the address or colour it refused; keep that beside the
      // button until the next save rather than letting it fade.
      setSaveMsg(e?.message || 'Save failed');
    }
    setSaving(false);
  }

  async function handleTest(e) {
    e.preventDefault();
    if (!testEmail.trim()) return;
    setTesting(true); setTestMsg('');
    try {
      await api.post(`email-templates/${template.context}/test`, { test_email: testEmail.trim() });
      setTestMsg(`Test sent to ${testEmail.trim()}`);
    } catch (err) {
      setTestMsg(err?.message || 'Send failed');
    }
    setTesting(false);
    setTimeout(() => setTestMsg(''), 5000);
  }

  const lastSent = stats?.last_sent ? fmtRelative(stats.last_sent) : null;
  const totalSent = stats?.total_sent || 0;
  const failCount = stats?.fail_count || 0;

  return (
    <>
      {previewOpen && (
        <PreviewModal context={template.context} onClose={() => setPreviewOpen(false)} />
      )}
      <div className={`border rounded-lg overflow-hidden ${isPaused ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200 bg-white'}`}>
        {/* Header row */}
        <div
          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50/60 select-none"
          onClick={() => setOpen(o => !o)}
        >
          <span className={`text-[9px] font-bold transition-transform duration-150 text-gray-400 ${open ? 'rotate-90' : ''}`}>▶</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] font-extrabold text-navy">{template.label}</span>
              {isPaused && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-bold text-[8px]">PAUSED</span>}
            </div>
            <p className="text-[8px] text-gray-500 mt-0.5 truncate">{template.trigger.desc}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
            {triggerBadge}
            {providerBadge}
            {lastSent && (
              <span className="text-[8px] text-gray-400">Last sent {lastSent}</span>
            )}
            {totalSent > 0 && (
              <span className="text-[8px] text-gray-400">
                {totalSent.toLocaleString()} sent
                {failCount > 0 && <span className="text-red-500 ml-1">· {failCount} failed</span>}
              </span>
            )}
            {!lastSent && totalSent === 0 && (
              <span className="text-[8px] text-gray-300">Never sent</span>
            )}
          </div>
        </div>

        {/* Expanded content */}
        {open && (
          <div className="border-t border-gray-100 px-4 py-3 space-y-4">
            {/* Details */}
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <div>
                <p className="text-[8px] font-bold text-gray-400 uppercase mb-1">Trigger</p>
                <p className="text-[9px] text-gray-700">{template.trigger.desc}</p>
                {template.trigger.cron && (
                  <p className="text-[8px] text-gray-400 font-mono mt-0.5">cron: {template.trigger.cron}</p>
                )}
              </div>
              <div>
                <p className="text-[8px] font-bold text-gray-400 uppercase mb-1">Recipients</p>
                <p className="text-[9px] text-gray-700">{template.recipients}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-[8px] font-bold text-gray-400 uppercase mb-1">Data in this email</p>
                <div className="flex flex-wrap gap-1">
                  {template.dataPoints.map(dp => (
                    <span key={dp} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[8px]">{dp}</span>
                  ))}
                </div>
              </div>
              {template.note && (
                <div className="md:col-span-2 text-[8px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  {template.note}
                </div>
              )}
            </div>

            {/* Preview button */}
            <div>
              <button
                onClick={() => setPreviewOpen(true)}
                className="px-3 py-1.5 text-[9px] font-semibold border border-gray-200 rounded hover:bg-gray-50"
              >
                Preview email
              </button>
            </div>

            {/* Configuration */}
            <div className="border border-gray-100 rounded-lg p-3 space-y-3">
              <p className="text-[9px] font-extrabold text-navy">Configuration</p>
              {isM365 && (
                <p className="text-[8px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  This email is sent via Microsoft 365 — header colour, extra recipients, and custom note overrides do not apply to M365 emails.
                </p>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {/* Header color */}
                <div>
                  <label className="text-[8px] font-bold text-gray-500 uppercase block mb-1">
                    Header Colour
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={headerColor || '#1B2A4A'}
                      onChange={e => setHeaderColor(e.target.value)}
                      disabled={isM365}
                      className="h-6 w-8 border border-gray-200 rounded cursor-pointer disabled:opacity-40"
                    />
                    <input
                      type="text"
                      value={headerColor}
                      onChange={e => setHeaderColor(e.target.value)}
                      placeholder="#1B2A4A (default)"
                      disabled={isM365}
                      className="border border-gray-200 rounded px-2 py-1 text-[9px] font-mono focus:outline-none focus:border-gold flex-1 disabled:opacity-40"
                    />
                    {headerColor && (
                      <button onClick={() => setHeaderColor('')} className="text-[8px] text-gray-400 hover:text-gray-600">✕</button>
                    )}
                  </div>
                </div>

                {/* Extra recipients */}
                <div>
                  <label className="text-[8px] font-bold text-gray-500 uppercase block mb-1">
                    Extra Recipients
                  </label>
                  <input
                    type="text"
                    value={extraRecipients}
                    onChange={e => setExtraRecipients(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                    disabled={isM365}
                    className="border border-gray-200 rounded px-2 py-1 text-[9px] focus:outline-none focus:border-gold w-full disabled:opacity-40"
                  />
                  <p className="text-[8px] text-gray-400 mt-0.5">Comma-separated. These addresses will be added to every email of this type.</p>
                </div>

                {/* Custom note */}
                <div className="md:col-span-2">
                  <label className="text-[8px] font-bold text-gray-500 uppercase block mb-1">
                    Custom Note
                  </label>
                  <textarea
                    value={customNote}
                    onChange={e => setCustomNote(e.target.value)}
                    placeholder="Optional text appended to the bottom of every email of this type…"
                    disabled={isM365}
                    rows={3}
                    className="border border-gray-200 rounded px-2 py-1.5 text-[9px] focus:outline-none focus:border-gold w-full resize-none disabled:opacity-40"
                  />
                  <p className="text-[8px] text-gray-400 mt-0.5">Plain text, line breaks kept. Shown in a highlighted box at the bottom of the email.</p>
                </div>

                {/* Pause toggle */}
                <div className="md:col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`pause-${template.context}`}
                    checked={isPaused}
                    onChange={e => setIsPaused(e.target.checked)}
                    disabled={isM365}
                    className="disabled:opacity-40"
                  />
                  <label htmlFor={`pause-${template.context}`} className={`text-[9px] font-semibold ${isM365 ? 'opacity-40' : 'cursor-pointer'} ${isPaused ? 'text-amber-700' : 'text-gray-700'}`}>
                    Pause this email type
                  </label>
                  {isPaused && <span className="text-[8px] text-amber-600">No emails of this type will be sent while paused</span>}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving || isM365}
                  className="px-3 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/90 disabled:opacity-40"
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                {saveMsg && (
                  <span className={`text-[9px] font-semibold ${saveMsg === 'Saved' ? 'text-green-600' : 'text-red-600'}`}>
                    {saveMsg}
                  </span>
                )}
              </div>
            </div>

            {/* Test send */}
            <div className="border border-gray-100 rounded-lg p-3">
              <p className="text-[9px] font-extrabold text-navy mb-2">Send Test Email</p>
              <p className="text-[8px] text-gray-500 mb-2">
                Sends a real email to the address below using sample / placeholder data. Use this to check formatting before the next live send.
              </p>
              <form onSubmit={handleTest} className="flex items-center gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="border border-gray-200 rounded px-2 py-1.5 text-[9px] focus:outline-none focus:border-gold flex-1"
                />
                <button
                  type="submit"
                  disabled={testing || !testEmail.trim()}
                  className="px-3 py-1.5 text-[9px] font-bold border border-navy text-navy rounded hover:bg-navy hover:text-white disabled:opacity-40 whitespace-nowrap"
                >
                  {testing ? 'Sending…' : 'Send Test'}
                </button>
              </form>
              {testMsg && (
                <p className={`text-[8px] mt-1.5 font-semibold ${testMsg.startsWith('Test sent') ? 'text-green-600' : 'text-red-600'}`}>
                  {testMsg}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Templates Tab ────────────────────────────────────────────────────────────

function TemplatesTab() {
  const [data, setData]       = useState({ configs: {}, stats: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all'); // all | cron | event | paused

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get('email-templates');
      setData(d || { configs: {}, stats: {} });
    } catch { setData({ configs: {}, stats: {} }); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = TEMPLATE_CATALOG.filter(t => {
    if (filter === 'cron')   return t.trigger.type === 'cron';
    if (filter === 'event')  return t.trigger.type === 'event';
    if (filter === 'paused') return !!data.configs[t.context]?.is_paused;
    return true;
  });

  const pausedCount = TEMPLATE_CATALOG.filter(t => data.configs[t.context]?.is_paused).length;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <p className="text-[9px] text-gray-500 flex-1">
          {TEMPLATE_CATALOG.length} email types — click any row to configure or preview
        </p>
        {pausedCount > 0 && (
          <span className="text-[8px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">
            {pausedCount} paused
          </span>
        )}
        <div className="flex gap-1">
          {['all', 'cron', 'event', 'paused'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2 py-0.5 text-[8px] rounded font-semibold border ${filter === f ? 'bg-navy text-white border-navy' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
              {f === 'all' ? 'All' : f === 'cron' ? 'Scheduled' : f === 'event' ? 'Event-triggered' : 'Paused'}
            </button>
          ))}
        </div>
        <button onClick={load} className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded hover:bg-gray-50">
          ↺
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[9px] text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(t => (
            <TemplateCard
              key={t.context}
              template={t}
              stats={data.stats[t.context]}
              config={data.configs[t.context]}
              onSave={load}
            />
          ))}
          {filtered.length === 0 && (
            <p className="text-center py-8 text-[9px] text-gray-400">No templates match this filter</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Root component ───────────────────────────────────────────────────────────

function MasterEmailSwitch() {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    api.get('app-settings/emails_master_enabled').then(r => setEnabled(r?.value !== '0')).catch(() => setEnabled(true));
  }, []);

  const toggle = async () => {
    setSaving(true);
    const next = !enabled;
    await api.put('app-settings/emails_master_enabled', { value: next ? '1' : '0' }).catch(() => {});
    setEnabled(next);
    setSaving(false);
  };

  if (enabled === null) return null;

  return (
    <div className={`rounded-xl border-2 p-4 flex items-center justify-between gap-4 mb-5 transition-colors ${enabled ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0 ${enabled ? 'bg-green-100' : 'bg-red-100'}`}>
          {enabled ? '✉' : '🔇'}
        </div>
        <div>
          <div className={`text-[11px] font-bold ${enabled ? 'text-green-800' : 'text-red-800'}`}>
            {enabled ? 'All automated emails are enabled' : 'All automated emails are suppressed'}
          </div>
          <div className={`text-[9px] mt-0.5 ${enabled ? 'text-green-600' : 'text-red-600'}`}>
            {enabled
              ? 'Emails are being sent normally. Turn off while importing historical data or testing.'
              : 'No emails are leaving the system — all sends are logged as suppressed. Turn on when ready to go live.'}
          </div>
        </div>
      </div>
      <button
        onClick={toggle}
        disabled={saving}
        className={`flex-shrink-0 px-4 py-2 rounded-lg text-[10px] font-bold transition-colors disabled:opacity-50 ${enabled ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-green-600 text-white hover:bg-green-700'}`}>
        {saving ? 'Saving…' : enabled ? 'Disable All Emails' : 'Enable All Emails'}
      </button>
    </div>
  );
}

// Pause only external-resource (contractor / resource-worker / supplier) emails,
// independently of the master switch — keeps that workflow's emails off while the
// rest of the org's email runs normally.
function ExternalResourceEmailSwitch() {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    api.get('app-settings/external_resource_emails_enabled').then(r => setEnabled(r?.value !== '0')).catch(() => setEnabled(true));
  }, []);

  const toggle = async () => {
    setSaving(true);
    const next = !enabled;
    await api.put('app-settings/external_resource_emails_enabled', { value: next ? '1' : '0' }).catch(() => {});
    setEnabled(next);
    setSaving(false);
  };

  if (enabled === null) return null;

  return (
    <div className={`rounded-xl border-2 p-4 flex items-center justify-between gap-4 mb-5 transition-colors ${enabled ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0 ${enabled ? 'bg-green-100' : 'bg-amber-100'}`}>
          {enabled ? '👷' : '🚧'}
        </div>
        <div>
          <div className={`text-[11px] font-bold ${enabled ? 'text-green-800' : 'text-amber-800'}`}>
            {enabled ? 'External-resource emails are enabled' : 'External-resource emails are paused'}
          </div>
          <div className={`text-[9px] mt-0.5 ${enabled ? 'text-green-600' : 'text-amber-700'}`}>
            Covers contractor timesheet reminders &amp; approvals, contractor portal invites, weekly-hours reminders and the supplier QA digest.
            {enabled
              ? ' Pause these while the external-resource workflow isn’t ready — other email keeps sending.'
              : ' These stay suppressed even when the master switch above is on.'}
          </div>
        </div>
      </div>
      <button
        onClick={toggle}
        disabled={saving}
        className={`flex-shrink-0 px-4 py-2 rounded-lg text-[10px] font-bold transition-colors disabled:opacity-50 ${enabled ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-green-600 text-white hover:bg-green-700'}`}>
        {saving ? 'Saving…' : enabled ? 'Pause External-Resource Emails' : 'Enable External-Resource Emails'}
      </button>
    </div>
  );
}

export default function SentEmailsPage() {
  const [tab, setTab] = useState('log');

  return (
    <div className="fade-in">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-extrabold text-navy">Email Management</h2>
          <p className="text-[9px] text-gray-500 mt-0.5">
            View sent email history, configure templates, and send test emails
          </p>
        </div>
      </div>
      <MasterEmailSwitch />
      <ExternalResourceEmailSwitch />

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-100 pb-0">
        {[
          { id: 'log',       label: 'Email Log' },
          { id: 'templates', label: 'Templates & Configuration' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-[9px] font-bold border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-navy text-navy'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'log'       && <EmailLogTab />}
      {tab === 'templates' && <TemplatesTab />}
    </div>
  );
}
